import './UrlClusters.less';
import { Button, Card, Form, Input, Select } from 'antd';
import React, { useState } from 'react';
import { PueLevel, UrlCluster } from './types';
import { PUE_LEVELS } from '../settings';
import { useUpdateUrlClusterMutation } from './service';

const { Option } = Select;

function getInitialInputState(text: string) {
    return {
        text,
        validationError: '',
    };
}

type Props = {
    websiteGuid: string;
    cluster: UrlCluster;
    onFinish: () => void;
};

export default function UpdateUrlCluster({ websiteGuid, cluster, onFinish }: Props) {
    const [titleInputState, setTitleInputState] = useState(() => getInitialInputState(cluster.title));
    const [descriptionInputState, setDescriptionInputState] = useState(() => getInitialInputState(cluster.description));
    const [selectedPue, setSelectedPue] = useState<PueLevel>(cluster.pue_level);
    const [pueReasonInput, setPueReasonInput] = useState<string>(cluster.pue_reason || '');

    const [updateUrlCluster, { isLoading: isUpdatingUrlCluster }] = useUpdateUrlClusterMutation();

    const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTitleInputState({ text: event.target.value, validationError: '' });
    };

    const handleDescriptionChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setDescriptionInputState({ text: event.target.value, validationError: '' });
    };

    const handlePueReasonChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setPueReasonInput(event.target.value);
    };

    const handleUpdateButtonClicked = async () => {
        let hasErrors = false;

        if (!titleInputState.text) {
            setTitleInputState({ ...titleInputState, validationError: 'Please provide a title' });
            hasErrors = true;
        }

        if (!descriptionInputState.text) {
            setDescriptionInputState({ ...descriptionInputState, validationError: 'Please provide a description' });
            hasErrors = true;
        }

        if (hasErrors) {
            return;
        }

        try {
            await updateUrlCluster({
                w_guid: websiteGuid,
                cluster_guid: cluster.guid,
                title: titleInputState.text,
                description: descriptionInputState.text,
                pue_level: selectedPue,
                pue_reason: pueReasonInput ? pueReasonInput : null,
            }).unwrap();

            onFinish();
        } catch (error) {
            setTitleInputState({ ...titleInputState, validationError: 'Something went wrong! Please try again or contact support.' });
        }
    };

    return (
        <Card bordered={false}>
            <Form
                className="update-url-cluster-form"
                layout="vertical"
            >
                <Form.Item
                    validateStatus={titleInputState.validationError ? 'error' : 'validating'}
                    help={titleInputState.validationError ? titleInputState.validationError : ''}
                >
                    <label htmlFor="title-input">Title</label>
                    <span className="label-description">A short title for the page group that reflects its content</span>
                    <Input
                        id="title-input"
                        value={titleInputState.text}
                        placeholder="Title"
                        onChange={handleTitleChange}
                    ></Input>
                </Form.Item>

                <Form.Item
                    validateStatus={descriptionInputState.validationError ? 'error' : 'validating'}
                    help={descriptionInputState.validationError ? descriptionInputState.validationError : ''}
                >
                    <label htmlFor="description-input">Description</label>
                    <span className="label-description">A short description of the page group</span>
                    <Input.TextArea
                        id="description-input"
                        value={descriptionInputState.text}
                        placeholder="Description"
                        onChange={handleDescriptionChange}
                        rows={3}
                    ></Input.TextArea>
                </Form.Item>

                <Form.Item>
                    <label htmlFor="puel">User Engagement Level</label>
                    <span className="label-description">This value should reflect how likely a user will will interact deeply with or spend significant time on the pages in this group</span>
                    <Select
                        id="puel"
                        value={selectedPue}
                        onChange={setSelectedPue}
                        style={{ fontSize: "16px" }}
                    >
                        {PUE_LEVELS.map((level) => (
                            <Option
                                key={level.value}
                                value={level.value}
                            >
                                {level.label}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item>
                    <label htmlFor="pue-reason-input">User Engagement Reason</label>
                    <span className="label-description">(Optional) - A short reason for choosing the user engagement level</span>
                    <Input.TextArea
                        id="pue-reason-input"
                        value={pueReasonInput}
                        placeholder="Reason"
                        onChange={handlePueReasonChange}
                        rows={5}
                    ></Input.TextArea>
                </Form.Item>

                <div className="form-button-group">
                    <Button
                        className="action-update-button"
                        type="primary"
                        size="small"
                        onClick={handleUpdateButtonClicked}
                        loading={isUpdatingUrlCluster}
                    >
                        Update
                    </Button>
                </div>
            </Form>
        </Card >
    );
}
